import { AICommandResult, Command, ICommandCanExecuteReturnValue, ICommandContext, UndoableCommand } from "../../commands/common/commands.js";
import { HierarchyNode } from "../../hierarchy/common/hierarchyService.js";
import { IProperty, IPropertyGroup, IPropertyService, isPropertyAddress, isPropertyLike, iterateProperties, PropertyAddress, ValueMetadata } from "./properties.js";
import { normalizeIncomingPath, splitAddress } from "./propertyPath.js";
import { resolvePropertyByPath } from "./resolvePropertyPath.js";
import { ILoggerService, ILogService } from "../../log/common/log.js";
import { IInstantiationService, ServicesAccessor } from "../../instantiation/common/instantiation.js";
import { IOperationService, originFromContext } from "../../../editor/services/operations/common/operations.js";
import { propertyOpTargetVia, type PropertyOpTarget } from "../../../editor/services/operations/common/propertyTarget.js";
import { serializeOpValue } from "../../../editor/services/operations/common/opValues.js";
import { IChangesetService } from "../../../editor/services/changeset/common/changeset.js";
import { IObjectTargetService } from "../../../editor/services/project/common/documentTarget.js";
import { IProjectService } from "../../../editor/services/project/common/project.js";
import { IHierarchyService } from "../../hierarchy/common/hierarchy.js";
import { safeStringify, truncateLargeValuesReplacer } from "../../../base/common/objects.js";
import { IClipboardService } from "../../clipboard/common/clipboardService.js";
import { INotificationService } from "../../notifications/common/notifications.js";
import { IAnalyticsService } from "../../analytics/common/analytics.js";
import { ContextMenuContext, isContextMenuContext } from "../../contextview/common/context.js";
import { MenuId, MenuRegistry } from "../../actions/common/actions.js";
import { parseAiTarget, resolveAiTarget, type AiTarget } from "../../ai/common/target.js";
import { handleOf } from "../../webgl/common/objectHandle.js";
import { IResourceService } from "../../resources/common/resources.js";
import { getNodeId, type GraphNode } from "@needle-tools/addressing";
import { isDocumentReferenceValue } from "@needle-tools/addressing/document";
import { updateObjectMatrices } from "../../webgl/common/objectMatrices.js";
// #region SetPropertyCommand

const OBJECT_TRANSFORM_KEYS = new Set(["position", "rotation", "quaternion", "scale"]);

export type ISetPropertyCommandArgs = {
	property: IProperty;
	value: any;
	/** Decode wire values at execution time, after resolving the property. */
	valueEncoding?: "document";
	previousValue?: any;
	metadata?: ValueMetadata;
	/**
	 * Hint for value conversion, e.g., "skybox" for HDRi textures that need equirectangular mapping.
	 * Passed through to accessors/converters to guide how values are processed.
	 */
	usageHint?: "skybox" | string;
}

type SetPropertyParametersForAI = Omit<ISetPropertyCommandArgs, 'property'> & {
	/** a document target from a search — `{ref?, address?}` (1h, the ADVERTISED
	 * shape; JSON string tolerated) */
	target?: AiTarget | string;
	/** path of the property within the target ("material/roughness") */
	path?: string;
	/** SESSION-INTERNAL address token — `<handle>` or `<handle>/<path>`; still
	 * the shape gizmos/tree/asset-drop pass programmatically, never advertised
	 * to the model */
	address?: string;
	propertyPath?: string;
}
export function isSetPropertyParametersForAI(obj: any): obj is SetPropertyParametersForAI {
	if (!obj || typeof obj !== "object" || !('value' in obj)) return false;
	return ('address' in obj && typeof obj.address === "string") || 'target' in obj;
}

/** the ELEMENT rung for target resolution: materials/textures/geometries live in
 * no scene graph — their stamped needle-id is matched across the weak registry */
function elementByNeedleId(accessor: ServicesAccessor, id: string | undefined): object | null {
	if (!id) return null;
	const resources = accessor.getIfExists(IResourceService) as { entries?(): Iterable<{ deref(): object | undefined }> } | undefined;
	for (const entry of resources?.entries?.() ?? []) {
		const resource = entry.deref();
		if (resource && getNodeId(resource as GraphNode) === id) return resource;
	}
	return null;
}

/**
 * A SESSION-INTERNAL address token → the `{ objectId, propertyPath }` the
 * property system takes. This is the shape programmatic callers (gizmos, tree,
 * asset drop) pass; the model-facing shape is `target` + `path` (1h), handled
 * in perform. Null when the arguments name no property — saying so beats
 * setting something arbitrary.
 */
export function normalizeSetPropertyArgs(args: SetPropertyParametersForAI): (SetPropertyParametersForAI & { objectId: string; propertyPath: string }) | null {
	const fromToken = args.address ? splitAddress(args.address) : null;
	const objectId = fromToken?.ownerId ?? args.address;
	const rawPath = fromToken?.path ?? args.propertyPath;
	if (objectId === undefined || !rawPath) return null;
	// a model types dots whatever the schema says — permanent tolerance
	const propertyPath = normalizeIncomingPath(rawPath) ?? rawPath;
	return { ...args, objectId: String(objectId), propertyPath };
}

/**
 * Command to set a property value.
 * Ensures changes propagate to TreeNode and the UI.
 */
export class SetPropertyCommand extends UndoableCommand<ISetPropertyCommandArgs | SetPropertyParametersForAI> {
	static readonly ID = 'property.setValue';

	constructor(
		// private readonly logger: ILogService,
		// private readonly propertyService: IPropertyService,
	) {
		super(SetPropertyCommand.ID, 'Set Property', {
			// property_read / property_find / property_write are ONE family to
			// the model — "setValue" broke that with a different verb and
			// casing (the command id stays: 60+ call sites edit through it)
			aiName: 'property_write',
			description: 'EDIT a property in the live scene — this is how you change anything. Takes a TARGET from hierarchy_search_nodes, resources_list, property_find or property_read plus a `path` and the new value. Works on objects AND on resources such as materials and textures. Vector values are objects like {"x":2,"y":2}.',
			args: {
				name: 'args',
				schema: {
					type: 'object',
					description: 'The property and value to set',
					properties: {
						target: {
							type: 'object',
							description: 'A TARGET returned by hierarchy_search_nodes, resources_list, property_find or property_read, passed back verbatim ({ref?, address?}). Never invent one: search for it.'
						},
						path: {
							type: 'string',
							description: 'The path of the property to modify, "/"-separated (e.g. "material/roughness", "materials/0/color").'
						},
						value: {
							// Removing type restriction to allow any value due to issue in vscode and cursor where union types fail https://linear.app/needle/issue/NE-6875
							// type: ['string', 'number', 'boolean', 'object', 'array', 'null'],
							description: 'The new value for the property can be of any type'
						},
						previousValue: {
							// Removing type restriction to allow any value due to issue in vscode and cursor where union types fail https://linear.app/needle/issue/NE-6875
							// type: ['string', 'number', 'boolean', 'object', 'array', 'null'],
							description: 'The previous value for the property (optional)'
						}
					},
					required: ['value']
				}
			},
			returns: 'void',
		});
	}

	canExecute(args: ISetPropertyCommandArgs | SetPropertyParametersForAI): ICommandCanExecuteReturnValue {

		if (isSetPropertyParametersForAI(args)) {
			return {
				canExecute: true
			}
		}

		// A caller passing the RETIRED objectId + propertyPath shape gets told what
		// to send. It used to fall through to "No property provided", which reads
		// like a missing argument rather than a stale call — and the light gizmos
		// reset the value before invoking the command, so a silent no-op looked
		// exactly like the edit snapping back.
		if ((args as { objectId?: unknown }).objectId !== undefined) {
			return {
				canExecute: false,
				reason: 'objectId + propertyPath is retired — pass `address` ("<handle>/material/roughness", built with buildAddress(handleOf(object), [...])) or a resolved `property`.'
			};
		}
		if (!args.property) {
			return {
				canExecute: false,
				reason: 'No property provided — pass a resolved `property`, or an `address` naming object and property.'
			};
		}
		if (!isPropertyLike(args.property)) {
			return {
				canExecute: false,
				reason: 'Invalid property object. Expected IProperty.'
			};
		}
		if (!args.property.editable) {
			return {
				canExecute: false,
				reason: `Property ${args.property.key} is not editable`
			};
		}
		return {
			canExecute: true
		};
	}

	private oldValue?: any;
	private replayTarget?: PropertyOpTarget;

	protected async perform(accessor: ServicesAccessor, args: ISetPropertyCommandArgs | SetPropertyParametersForAI, context?: ICommandContext, isUndo?: boolean): Promise<IProperty> {

		const propertyService = accessor.get(IPropertyService);
		const logger = accessor.get(ILogService);
		const operationService = accessor.getIfExists(IOperationService);

		if (isSetPropertyParametersForAI(args)) {
			const inst = accessor.get(IInstantiationService);
			// session-internal address shape first (gizmos, tree, asset drop);
			// the ADVERTISED shape is target + path (1h, document vocabulary)
			let resolved = normalizeSetPropertyArgs(args);
			/** the live object behind `target`, when the AI shape supplied one —
			 *  the property-collection walk needs it (see below) */
			let resolvedObject: object | null = null;
			if (!resolved && args.target !== undefined) {
				const parsed = parseAiTarget(args.target);
				const hierarchy = accessor.getIfExists(IHierarchyService) as IHierarchyService | undefined;
				const targets = accessor.getIfExists(IObjectTargetService) as IObjectTargetService | undefined;
				const object = parsed && hierarchy
					? resolveAiTarget(targets, hierarchy, parsed) ?? elementByNeedleId(accessor, parsed.address?.id)
					: null;
				resolvedObject = object as object | null;
				if (!object) {
					throw new Error(`Set property: the target could not be resolved. Call hierarchy_search_nodes, resources_list or property_find for a current target.`);
				}
				const rawPath = args.path ?? args.propertyPath;
				const objectId = handleOf(object as { id?: unknown; uuid?: unknown });
				if (rawPath && objectId) {
					resolved = { ...args, objectId, propertyPath: normalizeIncomingPath(rawPath) ?? rawPath };
				}
			}
			if (!resolved) {
				throw new Error(`Set property: the arguments name no property — pass a target from a search plus a path like "material/roughness".`);
			}
			// the SAME ladder the READ tools use — address lookup, then a walk
			// of the property collection. Without the walk the read tools
			// taught paths this write refused, and setting ONE component field
			// took six calls (#152, live 2026-08-31).
			const result = resolvedObject
				? (await resolvePropertyByPath(propertyService, resolvedObject, resolved.propertyPath, resolved.objectId))?.property ?? null
				: await propertyService.findProperty({ objectId: resolved.objectId, propertyPath: resolved.propertyPath });
			if (!result) {
				throw new Error(`Property at path ${resolved.propertyPath} not found on the target. Call hierarchy_search_nodes or property_find for a current target.`);
			}
			return inst.invokeFunction(a => {
				// context AND isUndo forwarded: dropping isUndo made an undo of
				// an address-shape edit re-run as a FRESH edit (emitting again)
				return this.perform(a, {
					property: result,
					value: args.value,
					valueEncoding: args.valueEncoding,
					previousValue: args.previousValue,
					metadata: args.metadata,
					usageHint: args.usageHint
				}, context, isUndo);
			});
		}

		const property = args.property;
		let value = args.value;
		if (args.valueEncoding === "document") {
			if (isDocumentReferenceValue(value)) {
				value = accessor.get(IObjectTargetService).objectFor(value);
				if (!value) throw new Error("Document property value refers to an unavailable object.");
			} else if (Array.isArray(value)) {
				const current = property.cloneValue();
				if (current && typeof current.fromArray === "function") value = current.fromArray(value);
			}
		}

		if (!isPropertyLike(property)) {
			throw new Error('Invalid property object. Expected IProperty.');
		}

		// Store old value for undo
		if (!isUndo) {
			if (args.previousValue !== undefined) {
				this.oldValue = args.previousValue;
			}
			else {
				this.oldValue = property.cloneValue();
			}

			// Don't do anything if value hasn't changed
			// TODO: comparison for complex types
			if (property.equalsValue(value, this.oldValue)) {
				logger.debug(`SetPropertyCommand: Value for property ${property.key} unchanged, skipping.`, typeof value !== "object" ? value : "");
				return property;
			}
		}

		const prev = this.oldValue;
		// conversion + consumer services, EAGER — the accessor dies at the await
		const changeset = accessor.getIfExists(IChangesetService) as IChangesetService | undefined;
		const opDeps = {
			targets: accessor.getIfExists(IObjectTargetService) as IObjectTargetService | undefined,
			logger: accessor.getIfExists(ILogService) as ILogService | undefined,
			projects: accessor.getIfExists(IProjectService) as IProjectService | undefined,
			hierarchy: accessor.getIfExists(IHierarchyService) as IHierarchyService | undefined,
			// H4: an unaddressable edit journals + dev-throws instead of only warning
			operations: operationService ?? undefined,
		};
        const editorScoped = property.metadata.editScope === "editor";
        const resolvedTarget = editorScoped ? null : propertyOpTargetVia(opDeps, property);
		this.replayTarget = resolvedTarget ?? undefined;
		return property.setValue({
			newValue: value,
			withoutNotify: true,
			// This is the command's own apply (both do and undo go through here) —
			// downstream of the guarded user write, so don't re-run the guard.
			skipApplyGuard: true,
			metadata: args.metadata,
			usageHint: args.usageHint
		}).then(_ => {
			if (OBJECT_TRANSFORM_KEYS.has(property.key) && (property.owner as { isObject3D?: boolean } | undefined)?.isObject3D === true) {
				updateObjectMatrices(property.owner);
			}
			property.notifyChanged({ value: true, previousValue: prev });
			// Configuration and other local editor state still use this command for
			// undo, but they are not scene-document edits and must not enter the
			// operation log, sync, save capture, or legacy changeset.
			if (editorScoped) return property;
			// emit onto the operation seam (the primitive; the project fold + future
			// undo/sync are consumers) — see plans/project-format-v0.md
			const origin = originFromContext(context);
			// UNDO: only the live restore happened here — the BASE retracts the
			// ops after this settles (H1). REDO: the base reinstates — this
			// guard only prevents emitting a NEWER op.
			if (isUndo || this.emittedOpIds.length > 0) {
				return property;
			}
			// the CHANGESET (live-scene summary; chrome's capture until #38) is fed
			// HERE, at the command's one emission point — ops are address-only now
			// and no longer carry the live property it needs
			changeset?.registerPropertyChange(property, prev);
			const emitted: string[] = [];
			// MATERIAL-valued edit = a material assign: classified at the PRODUCER
			// (every entry path — picker, inspector row, AI — arrives here), onto the
			// assign rail. The generic path cannot express a live Material.
			const materialValue = value as { isMaterial?: boolean; name?: string; userData?: Record<string, unknown> } | null | undefined;
			const ownerIsNode = (property.owner as { isObject3D?: boolean } | undefined)?.isObject3D === true;
			if (materialValue && typeof materialValue === "object" && materialValue.isMaterial === true && ownerIsNode) {
				const target = opDeps.targets?.targetFor(property.owner) ?? null;
				const stamped = materialValue.userData?.["needle-id"];
				if (target) {
					const op = operationService?.emit({
						kind: "assign-material",
						target,
						// session hint only — the writer recomputes the id at save time,
						// when the material's stamp state is final
						material: { id: typeof stamped === "string" ? stamped : "", name: materialValue.name || undefined },
					}, origin);
					if (op) emitted.push(op.id);
				}
				else {
					opDeps.logger?.warn(`Operations: material assign on '${(property.owner as { name?: string }).name || "object"}' is not addressable — NOT captured`);
					operationService?.reportCaptureMiss({ kind: "assign-material", reason: `material assign on '${(property.owner as { name?: string }).name || "object"}' is not addressable`, detail: { name: (property.owner as { name?: string }).name } });
				}
				this.emittedOpIds = emitted;
				return property;
			}
			// asset-sourced scene environment/background (an HDRI url with asset
			// identity): the assign op IS the capture — at the ONE choke point, so
			// drag-drop, quickpick, AI etc. all arrive here. NO set-property twin:
			// cloneValue snapshots the already-LOADED (PMREM) texture, the writer
			// never expressed that snapshot anyway, and a peer applying it live
			// broke every shader (env map with the wrong mapping/sampler).
			const asset = args.metadata?.asset;
			const ownerIsScene = (property.owner as { isScene?: boolean } | undefined)?.isScene === true;
			if (asset && ownerIsScene && (property.key === "background" || property.key === "environment")) {
				if (!resolvedTarget?.ref) {
					opDeps.logger?.warn("Operations: environment assign is not addressable — NOT captured");
					operationService?.reportCaptureMiss({ kind: "assign-environment", reason: "environment assign is not addressable" });
					return property;
				}
				const assignOp = operationService?.emit({
					kind: "assign-environment",
					target: { ...resolvedTarget, ref: resolvedTarget.ref },
					assetSrc: asset.src,
					assetName: args.metadata?.filename,
					assetIdentity: asset.id ? { id: asset.id, provider: asset.provider } : undefined,
					paths: [property.key],
				}, origin);
				if (assignOp) emitted.push(assignOp.id);
				this.emittedOpIds = emitted;
				return property;
			}
			const resolved = operationService ? resolvedTarget : null;
			if (resolved) {
				// values are SNAPSHOTS at emit in the DOCUMENT's canonical form
				// (serializeOpValue) — the evaluator patches arrays in place via
				// fromArray, while a raw row snapshot (a Color as hex) would get
				// assigned ONTO the receiver's field. `?? value` covers rows whose
				// cloneValue answers undefined for a null value (a CLEAR must
				// capture null, not vanish as "not expressible").
				const captured = (typeof property.cloneValue === "function" ? property.cloneValue() : undefined) ?? value;
				const encodeNodeReference = (object: object) => opDeps.targets?.targetFor(object) ?? null;
				const serialized = serializeOpValue(captured, { encodeNodeReference });
				if (!serialized.ok) {
					// never ship a value the evaluator cannot apply — the edit
					// stays local (the changeset above still captured it)
					opDeps.logger?.warn(`Operations: '${property.key}' edit is NOT captured as an operation — ${serialized.reason}`);
					operationService?.reportCaptureMiss({ kind: "set-property", reason: `'${property.key}' value is not expressible — ${serialized.reason}`, detail: { key: property.key } });
				}
				else {
					const previous = serializeOpValue(prev, { encodeNodeReference });
					const op = operationService?.emit({
						kind: "set-property",
						target: resolved.target,
						path: resolved.path,
						value: serialized.value,
						// undefined = "previous state not expressible": the remote
						// undo falls back to its survivor rule and warns
						previousValue: previous.ok ? previous.value : undefined,
					}, origin);
					if (op) emitted.push(op.id);
				}
			}
			this.emittedOpIds = emitted;

			// Sync TreeNode if this object has one
			if (property.key === 'name') {
				const owner = property.owner;
				const node = HierarchyNode.get(owner);
				if (node) {
					node.name = value;
				}
			}
			return property;
		});
	}

	executeForAI(accessor: ServicesAccessor, args: ISetPropertyCommandArgs, context?: ICommandContext): Promise<string> {
		return this.perform(accessor, args, context).then(property => {
			return `Property '${property?.metadata.address ?? property.key}' set successfully.`;
		});
	}


	protected undo(accessor: ServicesAccessor, args: ISetPropertyCommandArgs, context?: ICommandContext): Promise<void> {
		// restores the LIVE value (isUndo=true) — RETURNED so the base retracts
		// only after the async restore settled (H1); the command never emits an
		// inverse edit
		return this.perform(accessor, {
			...args,
			// swap value and previousValue
			value: this.oldValue,
			previousValue: args.value
		}, context, true).then(() => void 0);
	}

	/** the undo element captures state on a CLONE — clear the registry-held singleton
	 * (the base clears the op ids; this command also carries the pre-edit value) */
	override toAction(service: Parameters<UndoableCommand["toAction"]>[0], args: ISetPropertyCommandArgs | SetPropertyParametersForAI, context?: Parameters<UndoableCommand["toAction"]>[2]): ReturnType<UndoableCommand["toAction"]> {
		let replayArgs = args;
		if (this.replayTarget) {
			const { target, path } = this.replayTarget;
			replayArgs = { value: args.value, previousValue: args.previousValue, valueEncoding: args.valueEncoding,
				metadata: args.metadata, usageHint: args.usageHint,
				target: { ref: target.ref, address: target.address }, path: (target.pathPrefix ?? "") + path };
		}
		const action = super.toAction(service, replayArgs, context);
		this.oldValue = undefined;
		this.replayTarget = undefined;
		return action;
	}
}


// #region CopyPropertyGroupCommand

export interface CopyPropertyGroupCommandArgs {
}

export class CopyPropertyGroupCommand extends Command<CopyPropertyGroupCommandArgs> {
	static readonly ID = 'property.group.copy';

	constructor() {
		super(CopyPropertyGroupCommand.ID, 'Copy Property Group as JSON', {
			description: 'Copy all objects in a property group to clipboard as JSON',
		});
	}

	canExecute(args: unknown) {
		return { canExecute: true };
	}

	async execute(accessor: ServicesAccessor, args: unknown) {
		const logger = accessor.get(ILogService);
		const clipboard = accessor.get(IClipboardService);
		const notification = accessor.get(INotificationService);
		const telemetry = accessor.get(IAnalyticsService);

		let group: IPropertyGroup | null = null;

		if (isContextMenuContext(args)) {
			telemetry.logEvent('property.group.copy', { source: 'contextMenu' });
			// Find the property group in the targets
			const target = args.targets?.find(t => t && typeof t === 'object' && 'properties' in t && 'id' in t);
			if (target) {
				group = target as IPropertyGroup;
			} else {
				logger.error('No property group found in context menu targets.');
				notification.showNotification({
					key: 'copyPropertyGroupNoGroup',
					type: 'error',
					message: 'Cannot copy: No property group found.',
					position: 'cursor'
				});
				return;
			}
		}

		if (!group) {
			logger.error('No property group provided.');
			notification.showNotification({
				type: 'error',
				message: 'Cannot copy: No property group provided.'
			});
			return;
		}

		try {
			// Serialize all property values in the group
			const groupData: any = {
				groupName: group.name,
				groupId: group.id,
				properties: {}
			};

			for (const property of iterateProperties(group.properties)) {
				if (!property.hidden) {
					try {
						groupData.properties[property.key] = {
							value: property.value,
							type: property.type,
							label: property.label
						};
					} catch (err) {
						logger.warn(`Failed to read property ${property.key}:`, err);
					}
				}
			}

			// clipboard-bound: geometry attribute buffers must not dump whole
			// (a 174k-line normals array) — long number arrays get cut + marked
			const json = safeStringify(groupData, undefined, truncateLargeValuesReplacer());
			await clipboard.writeText(json);

			logger.info(`Copied property group "${group.name}" to clipboard.`);
			notification.showNotification({
				type: 'info',
				message: `Copied property group "${group.name}" to clipboard.`,
				position: 'cursor',
			});
		} catch (err) {
			telemetry.logEvent('property.group.copy.failed', {
				reason: 'exception',
				message: (err instanceof Error ? err.message : String(err)).substring(0, 500)
			});
			logger.error('Failed to copy property group to clipboard.', err);
			notification.showNotification({
				type: 'error',
				message: 'Failed to copy property group to clipboard: ' + (err instanceof Error ? err.message : String(err)).substring(0, 200),
				position: 'toast',
			});
		}
	}
}

MenuRegistry.appendMenuItem(MenuId.InspectorGroupHeaderContext, {
	command: {
		id: CopyPropertyGroupCommand.ID,
		title: 'Copy as JSON',
		icon: { id: 'copy' }
	},
	when: undefined,
	order: 10,
	group: '10_clipboard',
});
